/**
 * ResearchUserFeedbackPageV2 — anonymized feedback export tool.
 *
 * Layout:
 *   01 Filters — content session id (numeric), session name (contains),
 *      session type select, date range, include-quick-taps toggle chip.
 *      Coral Generate + ghost Refresh.
 *   02 Past exports — LibTable with status pill, filter summary,
 *      row/user counts, timestamps, download / preparing button.
 *
 * Same plumbing as v1: createFeedbackExport / checkFeedbackExportStatus
 * / fetchFeedbackExports / downloadFeedbackExport, with 5s polling and
 * a per-job failure counter that stops the poll after 5 consecutive
 * failures (parity).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { format, isValid as isValidDate } from 'date-fns';
import {
  createFeedbackExport,
  checkFeedbackExportStatus,
  fetchFeedbackExports,
  downloadFeedbackExport,
} from '../../../../actions/researchFeedbackActions';
import {
  PageHeader,
  Button,
  LibTable,
  StatusPill,
  cn,
} from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Select,
  DatePicker,
} from '../../../redesign/edit';

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);
const RefreshIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const DownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const SESSION_TYPES = [
  { value: '',           label: 'All' },
  { value: 'session',    label: 'Session (standalone)' },
  { value: 'series',     label: 'Series session' },
  { value: 'challenge',  label: 'Challenge session' },
];

const STATUS_TONE = {
  pending:    'amber',
  processing: 'blue',
  completed:  'green',
  failed:     'coral',
};

const MAX_POLL_FAILURES = 5;

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'MMM dd, yyyy HH:mm');
  } catch {
    return iso;
  }
}

function filterSummary(filters) {
  if (!filters || Object.keys(filters).length === 0) return 'All feedbacks';
  const parts = [];
  const contentId = filters.content_session_id ?? filters.session_id;
  if (contentId) parts.push(`content_session_id=${contentId}`);
  if (filters.session_name) parts.push(`name~"${filters.session_name}"`);
  if (filters.session_type) parts.push(`type=${filters.session_type}`);
  if (filters.from_date) parts.push(`from=${filters.from_date}`);
  if (filters.to_date) parts.push(`to=${filters.to_date}`);
  if (filters.include_quick_taps) parts.push('incl. quick taps');
  return parts.length ? parts.join(', ') : 'All feedbacks';
}

export default function ResearchUserFeedbackPageV2() {
  const dispatch = useDispatch();

  // Filters
  const [contentSessionId, setContentSessionId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [includeQuickTaps, setIncludeQuickTaps] = useState(false);

  // Trigger / polling
  const [isExporting, setIsExporting] = useState(false);

  // Past exports
  const [exports, setExports] = useState([]);
  const [isLoadingExports, setIsLoadingExports] = useState(false);
  const pollingRef = useRef({});
  const pollingFailuresRef = useRef({});

  // Download
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  const stopPolling = useCallback((jobId) => {
    if (pollingRef.current[jobId]) {
      clearInterval(pollingRef.current[jobId]);
      delete pollingRef.current[jobId];
    }
    delete pollingFailuresRef.current[jobId];
  }, []);

  const startPolling = useCallback(
    (jobId) => {
      if (pollingRef.current[jobId]) return;
      pollingFailuresRef.current[jobId] = 0;
      pollingRef.current[jobId] = setInterval(async () => {
        try {
          const updated = await dispatch(checkFeedbackExportStatus(jobId));
          if (!updated) {
            pollingFailuresRef.current[jobId] += 1;
            if (pollingFailuresRef.current[jobId] >= MAX_POLL_FAILURES) {
              stopPolling(jobId);
            }
            return;
          }
          pollingFailuresRef.current[jobId] = 0;
          setExports((prev) =>
            prev.map((j) => (j.job_id === jobId ? updated : j)),
          );
          if (updated.status === 'completed' || updated.status === 'failed') {
            stopPolling(jobId);
            if (updated.status === 'completed') {
              Swal.fire({
                icon: 'success',
                title: `Export #${jobId} ready`,
                text: `${updated.row_count || 0} rows`,
                timer: 1500,
                showConfirmButton: false,
              });
            } else {
              Swal.fire({
                icon: 'error',
                title: `Export #${jobId} failed`,
                text: updated.error_message || 'Unknown error',
              });
            }
          }
        } catch (e) {
          console.error(`Failed status check for feedback export #${jobId}:`, e);
          stopPolling(jobId);
        }
      }, 5000);
    },
    [dispatch, stopPolling],
  );

  const loadExports = useCallback(async () => {
    setIsLoadingExports(true);
    try {
      const data = await dispatch(fetchFeedbackExports());
      const list = Array.isArray(data) ? data : data?.data || [];
      setExports(list);
      list.forEach((job) => {
        if (
          (job.status === 'pending' || job.status === 'processing') &&
          !pollingRef.current[job.job_id]
        ) {
          startPolling(job.job_id);
        }
      });
    } finally {
      setIsLoadingExports(false);
    }
  }, [dispatch, startPolling]);

  useEffect(() => {
    loadExports();
    const intervals = pollingRef.current;
    return () => {
      Object.values(intervals).forEach((id) => clearInterval(id));
    };
  }, [loadExports]);

  const handleExport = useCallback(async () => {
    const filters = {};
    if (contentSessionId) filters.content_session_id = parseInt(contentSessionId, 10);
    if (sessionName.trim()) filters.session_name = sessionName.trim();
    if (sessionType) filters.session_type = sessionType;
    if (dateFrom && isValidDate(dateFrom)) filters.from_date = format(dateFrom, 'yyyy-MM-dd');
    if (dateTo && isValidDate(dateTo)) filters.to_date = format(dateTo, 'yyyy-MM-dd');
    if (includeQuickTaps) filters.include_quick_taps = true;

    setIsExporting(true);
    try {
      const job = await dispatch(createFeedbackExport(filters));
      if (job?.job_id) {
        Swal.fire({
          icon: 'info',
          title: `Export #${job.job_id} queued`,
          timer: 1500,
          showConfirmButton: false,
        });
        setExports((prev) => [job, ...prev]);
        startPolling(job.job_id);
      }
    } catch {
      // errorHandler already surfaces a Swal
    } finally {
      setIsExporting(false);
    }
  }, [dispatch, contentSessionId, sessionName, sessionType, dateFrom, dateTo, includeQuickTaps, startPolling]);

  const handleDownload = useCallback(
    async (job) => {
      setDownloadingIds((prev) => new Set([...prev, job.job_id]));
      try {
        const baseName = job.file_name || `research_feedback_export_${job.job_id}.csv`;
        const fileName = baseName.endsWith('.gz') ? baseName : `${baseName}.gz`;
        await dispatch(downloadFeedbackExport(job.job_id, fileName));
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(job.job_id);
          return next;
        });
      }
    },
    [dispatch],
  );

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Reports"
        title="Research User Feedback"
      />

      <div style={{ padding: '0 28px 60px', maxWidth: 1200 }}>
        <FormSection title="Filters" num="01">
          <FieldRow cols={3}>
            <FieldBlock label="Content session ID" hint="sessions.session_id (catalog)" htmlFor="f-rf-cid" noMargin>
              <TextInput
                id="f-rf-cid"
                type="number"
                value={contentSessionId}
                onChange={(e) => setContentSessionId(e.target.value)}
              />
            </FieldBlock>
            <FieldBlock label="Session name contains" htmlFor="f-rf-name" noMargin>
              <TextInput
                id="f-rf-name"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                placeholder='e.g. "You Are Enough"'
              />
            </FieldBlock>
            <FieldBlock label="Session type" htmlFor="f-rf-type" noMargin>
              <Select
                id="f-rf-type"
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
              >
                {SESSION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </FieldBlock>
          </FieldRow>
          <FieldRow>
            <FieldBlock label="From date" noMargin>
              <DatePicker value={dateFrom} onChange={setDateFrom} />
            </FieldBlock>
            <FieldBlock label="To date" noMargin>
              <DatePicker value={dateTo} onChange={setDateTo} />
            </FieldBlock>
          </FieldRow>

          <button
            type="button"
            onClick={() => setIncludeQuickTaps((v) => !v)}
            className={cn('flex items-center text-left transition-all duration-150')}
            style={{
              gap: 10,
              padding: '8px 12px',
              background: includeQuickTaps ? '#75A2931A' : '#FAF4EA',
              border: `1px solid ${includeQuickTaps ? '#75A293' : '#E8E0CF'}`,
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginTop: 12,
            }}
          >
            <span
              aria-hidden
              className="grid place-items-center shrink-0"
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: includeQuickTaps ? '#75A293' : 'transparent',
                border: `1.5px solid ${includeQuickTaps ? '#75A293' : '#C8BFA8'}`,
                color: '#fff',
                fontSize: 11,
                lineHeight: 1,
              }}
            >
              {includeQuickTaps && '✓'}
            </span>
            <span className="text-navy" style={{ fontSize: 13 }}>
              Include Quick Tap feedbacks <span className="text-text-muted">(excluded by default)</span>
            </span>
          </button>

          <div className="flex items-center" style={{ gap: 8, marginTop: 14 }}>
            <Button
              variant="coral"
              leadingIcon={PlusSign}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? 'Queuing…' : 'Generate export'}
            </Button>
            <Button
              variant="ghost"
              leadingIcon={RefreshIcon}
              onClick={loadExports}
              disabled={isLoadingExports}
            >
              Refresh
            </Button>
          </div>

          <p
            className="text-text-muted"
            style={{ fontSize: 12, marginTop: 14, marginBottom: 0 }}
          >
            The export contains anonymized rows only —{' '}
            <code className="font-mono text-navy">SHA-256(user_id)</code> matches
            the master research export. No names or emails included.
          </p>
        </FormSection>

        <FormSection
          title="Past exports"
          num="02"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {exports.length.toLocaleString()} total
            </span>
          }
        >
          {isLoadingExports && exports.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              Loading exports…
            </div>
          ) : exports.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              No exports yet — use Generate export to start one.
            </div>
          ) : (
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                <col style={{ width: 80 }} />
                <col style={{ width: 120 }} />
                <col />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 160 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>#</LibTable.Th>
                  <LibTable.CenterTh>Status</LibTable.CenterTh>
                  <LibTable.Th>Filters</LibTable.Th>
                  <LibTable.NumTh>Rows</LibTable.NumTh>
                  <LibTable.NumTh>Users</LibTable.NumTh>
                  <LibTable.Th>Created</LibTable.Th>
                  <LibTable.Th>Completed</LibTable.Th>
                  <LibTable.Th>Action</LibTable.Th>
                </tr>
              </LibTable.Head>
              <tbody>
                {exports.map((j) => {
                  const tone = STATUS_TONE[j.status] || 'gray';
                  const isDownloading = downloadingIds.has(j.job_id);
                  return (
                    <LibTable.Row key={j.job_id}>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5 }}>
                          {j.job_id}
                        </span>
                      </LibTable.Td>
                      <LibTable.CenterTd>
                        <StatusPill tone={tone}>{j.status}</StatusPill>
                      </LibTable.CenterTd>
                      <LibTable.Td>
                        <span className="text-text-muted truncate block" style={{ fontSize: 11.5 }} title={filterSummary(j.filters)}>
                          {filterSummary(j.filters)}
                        </span>
                      </LibTable.Td>
                      <LibTable.NumTd muted={j.row_count == null}>
                        {j.row_count != null ? j.row_count.toLocaleString() : '—'}
                      </LibTable.NumTd>
                      <LibTable.NumTd muted={j.unique_users == null}>
                        {j.unique_users != null ? j.unique_users.toLocaleString() : '—'}
                      </LibTable.NumTd>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(j.created_at)}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(j.completed_at)}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        {j.status === 'completed' ? (
                          <Button
                            variant="ghost"
                            leadingIcon={DownloadIcon}
                            onClick={() => handleDownload(j)}
                            disabled={isDownloading}
                          >
                            {isDownloading ? 'Preparing…' : 'Download'}
                          </Button>
                        ) : j.status === 'failed' ? (
                          <span className="text-coral" style={{ fontSize: 12 }}>
                            {j.error_message || 'Failed'}
                          </span>
                        ) : (
                          <span className="text-text-muted italic" style={{ fontSize: 12 }}>
                            In progress…
                          </span>
                        )}
                      </LibTable.Td>
                    </LibTable.Row>
                  );
                })}
              </tbody>
            </LibTable>
          )}
        </FormSection>
      </div>
    </div>
  );
}
